import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAP_ID } from '../config';

export type MarkerData = {
  id: string | number;
  lat: number;
  lng: number;
  title?: string;
  type?: 'attraction' | 'food' | 'hotel' | 'experience';
  isSaved?: boolean;
};

type MapViewLiteProps = {
  markers: MarkerData[];
  highlightedMarkerId?: string | number | null;
  highlightedId?: string | number | null; // Keep for backward compatibility
  hoveredMarkerId?: string | number | null; // For hover highlighting (no centering)
  onMarkerClick?: (id: string | number) => void;
  className?: string;
  center?: { lat: number; lng: number };
  zoom?: number;
  showEmptyState?: boolean; // Whether to show "No places found" message when markers array is empty
  loading?: boolean; // Whether places are currently being loaded
  interactive?: boolean; // Whether map should allow pan/zoom/drag (default: false for cost savings)
};

const libraries: ("drawing" | "geometry" | "places" | "visualization")[] = ["places", "geometry"];

// Generate marker icon based on type, highlight state, and saved state
const generateMarkerIcon = (type: 'attraction' | 'food' | 'hotel' | 'experience' | undefined, isHighlighted: boolean, isSaved: boolean = false) => {
  
  const size = 24; // Keep same size for circle
  const strokeWidth = isHighlighted ? 3 : 2;
  const fontSize = isHighlighted ? 14 : 12; // Make letter bigger when highlighted
  const iconSize = isHighlighted ? 10 : 8; // Icon size for special icons (smaller to fit in circle)
  
  // Color scheme: red when saved, blue for user location, blue when highlighted, gray when not
  let color;
  if (type === 'attraction' && !isSaved && !isHighlighted) {
    // Check if this is a user location marker by checking if isSaved and type are default
    // For user location, we'll use a different approach - render it specially in JSX
    color = { fill: '#6B7280', stroke: '#374151' }; // Gray when not highlighted
  } else {
    color = isSaved
    ? { fill: '#EF4444', stroke: '#DC2626' } // Red when saved
    : isHighlighted 
      ? { fill: '#3B82F6', stroke: '#1D4ED8' } // Blue when highlighted
      : { fill: '#6B7280', stroke: '#374151' }; // Gray when not highlighted
  }
  
  // Letter mapping
  const letters = {
    attraction: 'A',
    food: 'F', 
    hotel: 'H',
    experience: 'E'
  };
  
  const defaultLetter = '?';
  const letter = type ? letters[type] : defaultLetter;
  
  // Use house icon for hotels - white background with black house
  if (type === 'hotel') {
    const borderColor = isHighlighted ? '#3B82F6' : isSaved ? '#EF4444' : '#374151';
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="${size/2 - 2}" fill="white" stroke="${borderColor}" stroke-width="${strokeWidth}"/>
        <g transform="translate(${12 - iconSize/2}, ${12 - iconSize/2})">
          <path d="M${iconSize*0.5} ${iconSize*0.05}L${iconSize*0.95} ${iconSize*0.4}V${iconSize*0.95}H${iconSize*0.65}V${iconSize*0.6}H${iconSize*0.35}V${iconSize*0.95}H${iconSize*0.05}V${iconSize*0.4}L${iconSize*0.5} ${iconSize*0.05}Z" fill="none" stroke="black" stroke-width="0.8" stroke-linejoin="round"/>
        </g>
      </svg>
    `);
  }
  
  // Use camera icon for attractions - white background with black camera
  if (type === 'attraction') {
    const borderColor = isHighlighted ? '#3B82F6' : isSaved ? '#EF4444' : '#374151';
    const attractionIconSize = isHighlighted ? 14 : 12; // Bigger icon for attractions
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="${size/2 - 2}" fill="white" stroke="${borderColor}" stroke-width="${strokeWidth}"/>
        <g transform="translate(${12 - attractionIconSize/2}, ${12 - attractionIconSize/2})">
          <path d="M${attractionIconSize*0.35} ${attractionIconSize*0.15}L${attractionIconSize*0.45} ${attractionIconSize*0.25}H${attractionIconSize*0.55}L${attractionIconSize*0.65} ${attractionIconSize*0.15}" fill="none" stroke="black" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
          <rect x="${attractionIconSize*0.15}" y="${attractionIconSize*0.25}" width="${attractionIconSize*0.7}" height="${attractionIconSize*0.6}" rx="${attractionIconSize*0.08}" fill="none" stroke="black" stroke-width="0.7"/>
          <circle cx="${attractionIconSize*0.5}" cy="${attractionIconSize*0.55}" r="${attractionIconSize*0.18}" fill="none" stroke="black" stroke-width="0.7"/>
        </g>
      </svg>
    `);
  }
  
  // Use utensils icon for food - white background with black utensils
  if (type === 'food') {
    const borderColor = isHighlighted ? '#3B82F6' : isSaved ? '#EF4444' : '#374151';
    const foodIconSize = isHighlighted ? 15 : 13; // Bigger than attractions
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="${size/2 - 2}" fill="white" stroke="${borderColor}" stroke-width="${strokeWidth}"/>
        <g transform="translate(${12 - foodIconSize/2}, ${12 - foodIconSize/2})">
          <path d="M${foodIconSize*0.25} ${foodIconSize*0.1}L${foodIconSize*0.25} ${foodIconSize*0.4}M${foodIconSize*0.35} ${foodIconSize*0.1}L${foodIconSize*0.35} ${foodIconSize*0.4}M${foodIconSize*0.45} ${foodIconSize*0.1}L${foodIconSize*0.45} ${foodIconSize*0.4}M${foodIconSize*0.35} ${foodIconSize*0.4}L${foodIconSize*0.35} ${foodIconSize*0.9}" stroke="black" stroke-width="0.7" stroke-linecap="round"/>
          <path d="M${foodIconSize*0.65} ${foodIconSize*0.15}C${foodIconSize*0.65} ${foodIconSize*0.2} ${foodIconSize*0.6} ${foodIconSize*0.25} ${foodIconSize*0.6} ${foodIconSize*0.3}L${foodIconSize*0.6} ${foodIconSize*0.9}" stroke="black" stroke-width="0.7" stroke-linecap="round"/>
        </g>
      </svg>
    `);
  }
  
  // Use mountain icon for experience - white background with black mountain (lucide Mountain)
  if (type === 'experience') {
    const borderColor = isHighlighted ? '#3B82F6' : isSaved ? '#EF4444' : '#374151';
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="${size/2 - 2}" fill="white" stroke="${borderColor}" stroke-width="${strokeWidth}"/>
        <path d="m8 3 4 8 5-5 5 15H2L8 3z" fill="none" stroke="black" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" transform="translate(3, 3) scale(0.7)"/>
      </svg>
    `);
  }
  
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="${size/2 - 2}" fill="${color.fill}" stroke="${color.stroke}" stroke-width="${strokeWidth}"/>
      <text x="12" y="16" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="bold">${letter}</text>
    </svg>
  `);
};

export default function MapViewLite({
  markers,
  highlightedMarkerId,
  highlightedId,
  hoveredMarkerId,
  onMarkerClick,
  className = "",
  center = { lat: 7.8731, lng: 80.7718 }, // Sri Lanka center
  zoom = 8,
  showEmptyState = true, // Default to true for backward compatibility
  loading = false,
  interactive = false // Default to false for cost savings (non-interactive maps)
}: MapViewLiteProps) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script-mapviewlite',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
    mapIds: [GOOGLE_MAP_ID]
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapCenter, setMapCenter] = useState(center);
  const [hasAppliedSmartZoom, setHasAppliedSmartZoom] = useState(false);

  // Add touch event handling to prevent conflicts
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      // Check if the touch is on a map element
      const target = e.target as Element;
      if (target && target.closest('.gm-style')) {
        e.stopPropagation();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Check if the touch is on a map element
      const target = e.target as Element;
      if (target && target.closest('.gm-style')) {
        e.stopPropagation();
      }
    };

    // Add event listeners to document to handle touch events
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const currentHighlightedId = highlightedMarkerId || highlightedId; // For map centering (click)
  const visualHighlightId = hoveredMarkerId || currentHighlightedId; // For visual highlight (hover or click)

  // Center map on clicked marker when it changes (not on hover)
  useEffect(() => {
    // Center on highlighted marker when explicitly set (clicked)
    if (currentHighlightedId && mapRef.current && markers.length > 0) {
      const highlightedMarker = markers.find(m => m.id === currentHighlightedId);
      if (highlightedMarker) {
        mapRef.current.setCenter({ lat: highlightedMarker.lat, lng: highlightedMarker.lng });
        mapRef.current.setZoom(15); // Zoom in on the specific marker
      }
    }
  }, [currentHighlightedId, markers]);

  // Function to apply smart zoom based on marker distances
  const applySmartZoom = useCallback(() => {
    if (mapRef.current && markers.length > 0 && window.google && window.google.maps) {
      const bounds = new google.maps.LatLngBounds();
      markers.forEach(marker => {
        bounds.extend(new google.maps.LatLng(marker.lat, marker.lng));
      });
      
      // Calculate the distance between the furthest markers
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const distance = google.maps.geometry.spherical.computeDistanceBetween(ne, sw);
      
      // If markers are close together (less than 5km), zoom in more
      if (distance < 5000) { // 5km in meters
        mapRef.current.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
        // Additional zoom in for very close markers
        if (distance < 1000) { // Less than 1km
          setTimeout(() => {
            if (mapRef.current) {
              const currentZoom = mapRef.current.getZoom();
              if (currentZoom !== undefined && currentZoom < 15) {
                mapRef.current.setZoom(Math.min(currentZoom + 2, 15));
              }
            }
          }, 100);
        } else if (distance < 3000) { // Less than 3km
          setTimeout(() => {
            if (mapRef.current) {
              const currentZoom = mapRef.current.getZoom();
              if (currentZoom !== undefined && currentZoom < 15) {
                mapRef.current.setZoom(Math.min(currentZoom + 1, 15));
              }
            }
          }, 100);
        }
      } else {
        mapRef.current.fitBounds(bounds);
        
        // Prevent zooming out too far - enforce minimum zoom level for Sri Lanka
        window.google.maps.event.addListenerOnce(mapRef.current, 'bounds_changed', () => {
          const currentZoom = mapRef.current?.getZoom();
          if (currentZoom !== undefined) {
            // Minimum zoom: 7 (shows most of Sri Lanka)
            // Maximum zoom: 15 (reasonable city/area level)
            if (currentZoom < 7) {
              mapRef.current?.setZoom(7);
            } else if (currentZoom > 15) {
              mapRef.current?.setZoom(15);
            }
          }
        });
      }
      setHasAppliedSmartZoom(true);
    }
  }, [markers]);

  // Reset smart zoom flag when markers change
  useEffect(() => {
    setHasAppliedSmartZoom(false);
  }, [markers]);

  // Apply smart zoom when markers change or when map becomes visible
  // Only apply for multiple markers (not for single place view)
  useEffect(() => {
    if (markers.length > 1 && mapRef.current && !hasAppliedSmartZoom) {
      // Small delay to ensure map is fully rendered
      const timer = setTimeout(() => {
        applySmartZoom();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [markers, hasAppliedSmartZoom, applySmartZoom]);

  // Calculate center based on markers if available
  useEffect(() => {
    if (markers.length > 0 && isLoaded && window.google && window.google.maps) {
      const bounds = new google.maps.LatLngBounds();
      markers.forEach(marker => {
        bounds.extend(new google.maps.LatLng(marker.lat, marker.lng));
      });
      
      // Get center from bounds
      const boundsCenter = bounds.getCenter();
      setMapCenter({
        lat: boundsCenter.lat(),
        lng: boundsCenter.lng()
      });
    }
  }, [markers, isLoaded]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    
    // Apply smart zoom if multiple markers are available (not for single place view)
    if (markers.length > 1) {
      applySmartZoom();
    }
  }, [markers, applySmartZoom]);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleMarkerClick = useCallback((markerId: string | number) => {
    onMarkerClick?.(markerId);
  }, [onMarkerClick]);

  // Lite style configuration for clean, minimal look
  const liteMapOptions: google.maps.MapOptions = {
    mapId: GOOGLE_MAP_ID, // Add mapId here for Dynamic Maps pricing
    disableDefaultUI: true,
    mapTypeControl: false,
    streetViewControl: false,
    zoomControl: interactive, // Show zoom controls when interactive
    fullscreenControl: false,
    rotateControl: false,
    scaleControl: false,
    panControl: false,
    clickableIcons: false,
    keyboardShortcuts: false,
    // Conditionally enable/disable interactions based on interactive prop
    scrollwheel: interactive,
    disableDoubleClickZoom: !interactive,
    draggable: interactive,
    gestureHandling: interactive ? 'greedy' : 'none', // 'none' disables all pan/zoom gestures
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }] // Hide points of interest labels (cluttered)
      },
      {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: "off" }] // Hide transit labels
      },
      {
        featureType: "administrative.locality",
        elementType: "labels",
        stylers: [{ visibility: "on" }] // Show city names
      },
      {
        featureType: "administrative.province",
        elementType: "labels",
        stylers: [{ visibility: "on" }] // Show province names
      },
      {
        featureType: "road",
        elementType: "labels",
        stylers: [{ visibility: "on" }] // Show road names
      },
      {
        featureType: "road.arterial",
        elementType: "labels",
        stylers: [{ visibility: "on" }] // Show main roads
      },
      {
        featureType: "road.highway",
        elementType: "labels",
        stylers: [{ visibility: "on" }] // Show highways
      },
      {
        featureType: "water",
        elementType: "labels",
        stylers: [{ visibility: "off" }] // Hide water body labels
      },
      {
        featureType: "landscape",
        elementType: "geometry",
        stylers: [{ color: "#f5f5f5" }]
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#e0e0e0" }]
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#ffffff" }]
      },
      {
        featureType: "road",
        elementType: "geometry.stroke",
        stylers: [{ color: "#d0d0d0" }]
      }
    ]
  };

  if (loadError) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100`}>
        <div className="text-center">
          <div className="text-2xl mb-2">⚠️</div>
          <div className="text-gray-600">Error loading map</div>
          <div className="text-gray-400 text-sm mt-1">Please check your Google Maps API key</div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100`}>
        <div className="text-center">
          <div className="text-2xl mb-2">🗺️</div>
          <div className="text-gray-600">Loading map...</div>
          {!GOOGLE_MAPS_API_KEY && (
            <div className="text-red-500 text-sm mt-2">
              Google Maps API key not configured
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`${className} relative`}
      style={{
        touchAction: 'pan-x pan-y',
        overscrollBehavior: 'contain',
        isolation: 'isolate' // Creates a new stacking context to isolate touch events
      }}
      onTouchStart={(e) => {
        // Prevent touch event conflicts with parent scroll containers
        e.stopPropagation();
      }}
      onTouchMove={(e) => {
        // Allow map to handle its own touch events
        e.stopPropagation();
      }}
      onTouchEnd={(e) => {
        // Allow map to handle its own touch events
        e.stopPropagation();
      }}
      onTouchCancel={(e) => {
        // Handle touch cancel events to prevent conflicts
        e.stopPropagation();
      }}
    >
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={mapCenter}
        zoom={zoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={liteMapOptions}
      >
        {markers.map((marker, index) => {
          // Special handling for user location marker
          if (marker.id === 'user-location') {
            return (
              <Marker
                key={`marker-${marker.id}-${index}`}
                position={{ lat: marker.lat, lng: marker.lng }}
                title={marker.title || 'Your Location'}
                onClick={() => handleMarkerClick(marker.id)}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: '#A855F7',
                  fillOpacity: 1,
                  strokeColor: '#7E22CE',
                  strokeWeight: 2
                }}
              />
            );
          }
          
          // Standard marker rendering for places
          return (
          <Marker
            key={`marker-${marker.id}-${index}`}
            position={{ lat: marker.lat, lng: marker.lng }}
            title={marker.title || `Marker ${marker.id}`}
            onClick={() => handleMarkerClick(marker.id)}
            icon={{
              url: generateMarkerIcon(marker.type, visualHighlightId === marker.id, marker.isSaved),
              scaledSize: new google.maps.Size(24, 24),
              anchor: new google.maps.Point(12, 12)
            }}
          />
          );
        })}
      </GoogleMap>
      
      {/* Fallback message when no markers (only if showEmptyState is true) */}
      {markers.length === 0 && showEmptyState && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 bg-opacity-95">
          <div className="text-center px-6">
            {loading ? (
              <>
                {/* Modern loading state */}
                <div className="relative mb-4">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl">🗺️</span>
                  </div>
                </div>
                <div className="text-gray-800 font-semibold text-lg mb-2">Looking for amazing places for you</div>
                <div className="text-gray-600 text-sm">Discovering the best spots in Sri Lanka...</div>
              </>
            ) : (
              <>
                {/* No places state */}
                <div className="text-4xl mb-3">🗺️</div>
                <div className="text-gray-700 font-medium text-base mb-2">No places found</div>
                <div className="text-gray-500 text-sm">Try adjusting your filters or search criteria</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
